import * as D from '@radix-ui/react-dialog';
import { Link, useRouterState } from '@tanstack/react-router';
import { ArrowRight, CircleHelp, Lightbulb, Rocket, Search, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/ui/button';

/**
 * Page help: what the page is for, the first three things to do, one tip. The text lives in the locales under help.<page>;
 * a page without its own entry gets the general help. Longest matching path wins (/sales/new before /sales).
 */
const PAGES: Array<[RegExp, string]> = [
  [/^\/sales\/(new|[^/]+\/edit)$/, 'salesEditor'],
  [/^\/sales\/commissions/, 'commissions'],
  [/^\/sales\/promotions/, 'promotions'],
  [/^\/sales\/grouped-invoicing/, 'groupedInvoicing'],
  [/^\/sales/, 'sales'],
  [/^\/onboarding/, 'onboarding'],
  [/^\/parties/, 'parties'],
  [/^\/products/, 'products'],
  [/^\/payments/, 'payments'],
  [/^\/purchasing/, 'purchasing'],
  [/^\/inventory/, 'inventory'],
  [/^\/treasury|^\/bank/, 'treasury'],
  [/^\/accounting/, 'accounting'],
  [/^\/vat/, 'vat'],
  [/^\/withholding/, 'withholding'],
  [/^\/einvoice/, 'einvoice'],
  [/^\/reports/, 'reports'],
  [/^\/company/, 'company'],
  [/^\/fiscal/, 'fiscal'],
  [/^\/users/, 'users'],
  [/^\/document-settings/, 'documentSettings'],
  [/^\/settings\/series/, 'series'],
  [/^\/settings\/print-layouts/, 'printLayouts'],
  [/^\/crm/, 'crm'],
  [/^\/pos/, 'pos'],
  [/^\/$/, 'dashboard'],
];
export const helpPageFor = (path: string) => PAGES.find(([rx]) => rx.test(path))?.[1] ?? 'general';

export function HelpButton({ onSearch }: { onSearch: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const page = helpPageFor(path);
  const k = (f: string) => `help.${page}.${f}`;
  return (
    <D.Root open={open} onOpenChange={setOpen}>
      <D.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('help.open')} title={t('help.open')}><CircleHelp /></Button>
      </D.Trigger>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-fg/30" />
        <D.Content className="animate-in fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col border-s border-border bg-surface shadow-lg outline-none">
          <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <p className="text-xs font-medium text-muted">{t('help.label')}</p>
              <D.Title className="text-base font-semibold text-fg">{t(k('title'))}</D.Title>
            </div>
            <D.Close asChild><Button variant="ghost" size="icon" aria-label={t('help.close')}><X /></Button></D.Close>
          </div>
          <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
            <D.Description className="text-sm leading-relaxed text-fg">{t(k('purpose'))}</D.Description>
            <section>
              <h3 className="mb-2 text-sm font-semibold text-fg">{t('help.firstSteps')}</h3>
              <ol className="space-y-2">
                {(['s1', 's2', 's3'] as const).map((s, i) => (
                  <li key={s} className="flex gap-3 text-sm text-fg">
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-accent-soft text-xs font-semibold text-accent tnum">{i + 1}</span>
                    <span className="pt-0.5">{t(k(s))}</span>
                  </li>
                ))}
              </ol>
            </section>
            <section className="flex gap-3 rounded-lg border border-warning-border bg-warning-soft p-3 text-sm text-fg">
              <Lightbulb className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
              <p>{t(k('tip'))}</p>
            </section>
          </div>
          <div className="space-y-2 border-t border-border px-5 py-4">
            <Button variant="secondary" className="w-full justify-between" onClick={() => { setOpen(false); onSearch(); }}>
              <span className="flex items-center gap-2"><Search className="size-4" aria-hidden />{t('help.search')}</span>
              <kbd className="rounded border border-border bg-surface-2 px-1.5 font-mono text-[11px]">Ctrl K</kbd>
            </Button>
            <Button variant="ghost" className="w-full justify-between" asChild>
              <Link to="/onboarding" onClick={() => setOpen(false)}>
                <span className="flex items-center gap-2"><Rocket className="size-4" aria-hidden />{t('help.setupGuide')}</span>
                <ArrowRight className="size-4 rtl:rotate-180" aria-hidden />
              </Link>
            </Button>
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
